import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, AlertCircle, Check } from 'lucide-react';
import { Button } from './button';
import { Progress } from './progress';
import { useLanguage } from '@/contexts/LanguageContext';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  selectedFile: File | null;
  accept?: string;
  maxSize?: number; // in bytes
  isUploading?: boolean;
  uploadProgress?: number;
  error?: string;
  label: string;
  description?: string;
  required?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onFileRemove,
  selectedFile,
  accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png",
  maxSize = 10 * 1024 * 1024, // 10MB default
  isUploading = false,
  uploadProgress = 0,
  error,
  label,
  description,
  required = false,
}) => {
  const { t, isRTL } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize) {
      return t('auth.fileSizeError') || 
             `حجم الملف يجب أن لا يتجاوز ${formatFileSize(maxSize)}`;
    }
    
    const acceptedTypes = accept.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type.toLowerCase();
      }
      return file.type.match(type.replace('*', '.*'));
    })) {
      return t('auth.fileTypeError') || 'نوع الملف غير مدعوم';
    }
    
    return null;
  };

  const handleFileSelect = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      return;
    }
    onFileSelect(file);
  }, [onFileSelect, maxSize, accept, t]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleClick = () => {
    if (!isUploading && !selectedFile) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveFile = () => {
    if (!isUploading) {
      onFileRemove();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-2">
      <label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'} block`}>
        {label}
        {required && <span className="text-red-500 mr-1">*</span>}
      </label>
      
      {description && (
        <p className="text-xs text-muted-foreground mb-2">{description}</p>
      )}

      {!selectedFile ? (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragging 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
            }
            ${error ? 'border-red-500 bg-red-50' : ''}
            ${isUploading ? 'cursor-not-allowed opacity-50' : ''}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileInputChange}
            className="hidden"
            disabled={isUploading}
          />
          
          <div className="space-y-3">
            <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                {t('auth.dragDropFile') || 'اسحب الملف هنا أو انقر للاختيار'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('auth.supportedFormats') || 
                 `الصيغ المدعومة: ${accept}`}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('auth.maxFileSize') || 
                 `الحد الأقصى: ${formatFileSize(maxSize)}`}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 space-x-reverse">
              <div className="flex-shrink-0">
                {isUploading ? (
                  <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
                ) : (
                  <File className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>
            
            {!isUploading && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          {isUploading && (
            <div className="space-y-2">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {t('auth.uploading') || 'جاري الرفع...'} {uploadProgress}%
              </p>
            </div>
          )}
          
          {!isUploading && (
            <div className="flex items-center text-green-600 text-xs">
              <Check className="h-3 w-3 mr-1" />
              {t('auth.fileReady') || 'الملف جاهز للرفع'}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center text-red-600 text-xs mt-1">
          <AlertCircle className="h-3 w-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
